import { useEffect, useMemo, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bot } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { useGlobalChat } from "@/hooks/use-global-chat";
import { useChatContext, useDocumentIds } from "@/stores/chat-context-store";
import { ChatMessageBubble } from "@/components/documents/chat/ChatMessageBubble";
import { ChatStreamingIndicator } from "@/components/documents/chat/ChatStreamingIndicator";
import { ChatThreadHeader } from "@/components/documents/chat/ChatThreadHeader";
import { ChatPanel } from "./index";
import { ChatContextChips } from "./ChatContextChips";
import { GlobalChatInput } from "./GlobalChatInput";
import type { Id } from "@/packages/types";
import type { QuickAction } from "./types";

/* ------------------------------------------------------------------ */
/* Quick action sets (translation-aware)                                */
/* ------------------------------------------------------------------ */

function useQuickActionSets() {
  const { t } = useTranslation(["chat"]);

  const DOCUMENT_ACTIONS: { label: string; prompt: string }[] = [
    {
      label: t("chat:quickActions.documents.summarize"),
      prompt: "Summarize this document",
    },
    {
      label: t("chat:quickActions.documents.findChecklist"),
      prompt: "What checklist items does this document satisfy?",
    },
    {
      label: t("chat:quickActions.documents.whatCovers"),
      prompt: "What topics and requirements does this document cover?",
    },
  ];

  const AUDITS_PAGE_ACTIONS: { label: string; prompt: string }[] = [
    {
      label: t("chat:quickActions.audits.progress"),
      prompt: "Give me an overview of my audit progress",
    },
    {
      label: t("chat:quickActions.audits.incomplete"),
      prompt: "What checklist items are still missing or incomplete?",
    },
    {
      label: t("chat:quickActions.audits.coverageGaps"),
      prompt: "Which checklist items don't have linked documents yet?",
    },
  ];

  const DOCUMENTS_PAGE_ACTIONS: { label: string; prompt: string }[] = [
    {
      label: t("chat:quickActions.documentsPage.summarize"),
      prompt: "Give me a summary of all my documents",
    },
    {
      label: t("chat:quickActions.documentsPage.needsReview"),
      prompt: "Which documents need attention or are missing summaries?",
    },
    {
      label: t("chat:quickActions.documentsPage.complianceGaps"),
      prompt: "What compliance gaps exist across my documents?",
    },
  ];

  const HOME_PAGE_ACTIONS: { label: string; prompt: string }[] = [
    {
      label: t("chat:quickActions.home.overview"),
      prompt: "Give me an overview of my audits and documents",
    },
    {
      label: t("chat:quickActions.home.needsAttention"),
      prompt: "What should I focus on right now?",
    },
    {
      label: t("chat:quickActions.home.recentActivity"),
      prompt: "Show me what happened recently across my audits",
    },
  ];

  const DEFAULT_ACTIONS: { label: string; prompt: string }[] = [
    {
      label: t("chat:quickActions.documentsPage.summarize"),
      prompt: "Give me a summary of all my documents",
    },
    {
      label: t("chat:quickActions.documentsPage.complianceGaps"),
      prompt: "What compliance gaps exist across my documents?",
    },
    {
      label: t("chat:quickActions.home.needsAttention"),
      prompt: "Which documents need my attention and why?",
    },
  ];

  return {
    DOCUMENT_ACTIONS,
    AUDITS_PAGE_ACTIONS,
    DOCUMENTS_PAGE_ACTIONS,
    HOME_PAGE_ACTIONS,
    DEFAULT_ACTIONS,
  };
}

function GlobalChatEmptyState() {
  const { t } = useTranslation(["chat"]);
  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent/10">
        <Bot size={20} className="text-accent" />
      </div>
      <p className="text-sm text-muted">
        {t("chat:assistant.globalEmptyState")}
      </p>
    </div>
  );
}

export function GlobalChatPanel() {
  const { t } = useTranslation(["chat"]);
  const documentIds = useDocumentIds();
  const typedDocumentIds = documentIds as Id<"documents">[];
  const entities = useChatContext((s) => s.entities);
  const addEntity = useChatContext((s) => s.addEntity);
  const {
    DOCUMENT_ACTIONS,
    AUDITS_PAGE_ACTIONS,
    DOCUMENTS_PAGE_ACTIONS,
    HOME_PAGE_ACTIONS,
    DEFAULT_ACTIONS,
  } = useQuickActionSets();

  const {
    messages,
    sendMessage,
    isStreaming,
    isSending,
    isLoading,
    threads,
    threadId,
    startNewThread,
    selectThread,
    deleteThread,
    renameThread,
  } = useGlobalChat(typedDocumentIds);

  const bottomRef = useRef<HTMLDivElement>(null);
  const processedToolPartsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  // Auto-context: when agent tool results contain documents, add them to context
  useEffect(() => {
    for (const msg of messages) {
      if (msg.role !== "assistant" || !msg.toolParts) continue;
      for (const part of msg.toolParts) {
        const partKey = `${msg.id}-${part.toolName}`;
        if (processedToolPartsRef.current.has(partKey)) continue;
        processedToolPartsRef.current.add(partKey);

        if (part.toolName === "searchDocuments" && Array.isArray(part.output)) {
          for (const result of part.output as Array<Record<string, unknown>>) {
            const docId = String(result._id ?? result.id ?? "");
            const docName = String(
              result.fileName ?? result.title ?? t("chat:entities.document"),
            );
            if (docId) {
              addEntity({ type: "document", id: docId, label: docName });
            }
          }
        }

        if (
          part.toolName === "Details" &&
          typeof part.output === "object" &&
          part.output !== null
        ) {
          const doc = part.output as Record<string, unknown>;
          const docId = String(doc._id ?? doc.id ?? "");
          const docName = String(
            doc.fileName ?? doc.title ?? t("chat:entities.document"),
          );
          if (docId) {
            addEntity({ type: "document", id: docId, label: docName });
          }
        }

        // Auto-context: audit entities from tool results
        if (part.toolName === "listAudits" && Array.isArray(part.output)) {
          for (const result of part.output as Array<Record<string, unknown>>) {
            const auditId = String(result.id ?? "");
            const auditName = String(
              result.displayName ?? t("chat:entities.audit"),
            );
            if (auditId) {
              addEntity({ type: "audit", id: auditId, label: auditName });
            }
          }
        }

        if (
          part.toolName === "getAuditDetail" &&
          typeof part.output === "object" &&
          part.output !== null
        ) {
          const audit = part.output as Record<string, unknown>;
          const auditId = String(audit.id ?? "");
          const auditName = String(
            audit.displayName ?? t("chat:entities.audit"),
          );
          if (auditId) {
            addEntity({ type: "audit", id: auditId, label: auditName });
          }
        }

        // Auto-context: checklist-item entities from tool results
        if (
          part.toolName === "getChecklistItemDetail" &&
          typeof part.output === "object" &&
          part.output !== null
        ) {
          const item = part.output as Record<string, unknown>;
          const itemId = String(item.id ?? "");
          const itemDesc = String(
            (item.description as string)?.slice(0, 60) ??
              t("chat:entities.checklistItem"),
          );
          if (itemId) {
            addEntity({ type: "checklist-item", id: itemId, label: itemDesc });
          }
        }
      }
    }
  }, [messages, addEntity, t]);

  const hasMessages = messages.length > 0;
  const hasDocumentEntities = entities.some((e) => e.type === "document");
  const ambient = useChatContext((s) => s.ambient);

  // Quick actions: priority cascade based on context and current page
  const quickActions: QuickAction[] = useMemo(() => {
    // Priority 1: specific documents in context → document-focused actions
    if (hasDocumentEntities) {
      return DOCUMENT_ACTIONS.map((a) => ({
        label: a.label,
        onClick: () => sendMessage(a.prompt),
      }));
    }
    // Priority 2: per-page actions based on current route
    const pageActionsMap: Record<string, { label: string; prompt: string }[]> =
      {
        audits: AUDITS_PAGE_ACTIONS,
        documents: DOCUMENTS_PAGE_ACTIONS,
        home: HOME_PAGE_ACTIONS,
      };
    const pageActions = pageActionsMap[ambient?.page ?? ""] ?? DEFAULT_ACTIONS;
    return pageActions.map((a) => ({
      label: a.label,
      onClick: () => sendMessage(a.prompt),
    }));
  }, [
    hasDocumentEntities,
    ambient?.page,
    sendMessage,
    AUDITS_PAGE_ACTIONS,
    DOCUMENTS_PAGE_ACTIONS,
    HOME_PAGE_ACTIONS,
    DEFAULT_ACTIONS,
    DOCUMENT_ACTIONS,
  ]);

  return (
    <ChatPanel>
      <ChatPanel.Header title={t("chat:assistant.title")} />

      {/* Thread selector header — shown when threads exist */}
      {threads.length > 0 ? (
        <ChatThreadHeader
          threads={threads}
          activeThreadId={threadId}
          onNewThread={startNewThread}
          onSelectThread={selectThread}
          onDeleteThread={deleteThread}
          onRenameThread={renameThread}
        />
      ) : null}

      {/* Messages area */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <motion.div
            className="flex items-center gap-2 text-muted"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="h-4 w-4 border-2 border-muted/30 border-t-muted rounded-full animate-spin" />
            <span className="text-sm">{t("chat:assistant.loading")}</span>
          </motion.div>
        </div>
      ) : !hasMessages ? (
        <>
          <GlobalChatEmptyState />
          {/* Quick actions only shown when no messages */}
          <ChatPanel.QuickActions actions={quickActions} />
        </>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.map((message, i) => (
            <ChatMessageBubble
              key={message.id}
              message={message}
              index={i}
              isStreaming={
                isStreaming &&
                i === messages.length - 1 &&
                message.role === "assistant"
              }
            />
          ))}
          <AnimatePresence>
            {isStreaming && <ChatStreamingIndicator />}
          </AnimatePresence>
          <div ref={bottomRef} />
        </div>
      )}

      {/* Context chips above input */}
      <ChatContextChips />

      {/* Input bar with @-mention support */}
      <GlobalChatInput
        placeholder={
          isSending
            ? t("chat:input.sending")
            : t("chat:input.mentionPlaceholder")
        }
        onSend={sendMessage}
      />
    </ChatPanel>
  );
}
